import { DESIGN_HEIGHT, DESIGN_WIDTH, frontalQuad, project, toPoints } from '../geometry';
import type { Vec3 } from '../geometry';
import { boxFaces, scope, seeded, worldPoints } from './kit';
import { PAL } from './palette';
import { overlayQuad } from './regions';
import {
  STAIRS,
  staircaseCamera as cam,
  staircaseRegions,
  stepY,
  stepZ,
} from './staircase.geometry';
import type { ArtProps } from './types';

const { steps, rise, going, half, zBack, ceiling } = STAIRS;
const P = (x: number, y: number, z: number) => project(cam, [x, y, z]);
const landingY = stepY(steps);
/** Where the flight ends and the landing begins. */
const zLanding = stepZ(steps - 1);

/** Height of a line that follows the nosings at `offset` above them (handrail, wall lights), at depth `z`. */
const slope = (z: number, offset: number): number =>
  z <= zLanding ? offset + rise + z * (rise / going) : offset + landingY;

/** One leaf, from its base along `angle` degrees (−90 = up). */
function leaf(x: number, y: number, angle: number, length: number, width: number): string {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const f = (value: number): string => value.toFixed(1);
  const tipX = x + dx * length;
  const tipY = y + dy * length;
  const midX = x + dx * length * 0.5;
  const midY = y + dy * length * 0.5;
  return `M${f(x)} ${f(y)}Q${f(midX - dy * width)} ${f(midY + dx * width)} ${f(tipX)} ${f(tipY)}Q${f(midX + dy * width)} ${f(midY - dx * width)} ${f(x)} ${f(y)}Z`;
}

/** The saw-tooth profile of the flight along a wall (`x`), lifted by `lift` metres. */
function profile(x: number, lift: number): Vec3[] {
  const points: Vec3[] = [];
  for (let i = 0; i < steps; i += 1) {
    points.push([x, stepY(i) + lift, stepZ(i)], [x, stepY(i + 1) + lift, stepZ(i)]);
  }
  points.push([x, landingY + lift, zBack]);
  return points;
}

// ── Back: the stairwell ─────────────────────────────────────────────────────────────────────────

export function StaircaseBack({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'back');
  const zNear = -0.8;
  const ceilingPoly: Vec3[] = [
    [-half, ceiling, zNear],
    [half, ceiling, zNear],
    [half, ceiling, zBack],
    [-half, ceiling, zBack],
  ];
  const leftWall: Vec3[] = [
    [-half, ceiling, zNear],
    [-half, ceiling, zBack],
    [-half, 0, zBack],
    [-half, 0, zNear],
  ];
  const rightWall: Vec3[] = [
    [half, ceiling, zBack],
    [half, ceiling, zNear],
    [half, 0, zNear],
    [half, 0, zBack],
  ];
  const backWall = frontalQuad(cam, -half, half, landingY, ceiling, zBack);
  const doorway = frontalQuad(cam, -0.62, 0.62, landingY, 2.75, zBack - 0.01);
  const doorLight = P(0, landingY + 0.9, zBack);

  // skirting that follows the steps on both walls
  const skirt = (x: number): string => {
    const lower = profile(x, 0);
    const upper = profile(x, 0.13).reverse();
    return worldPoints(cam, [...lower, ...upper]);
  };

  // wall panelling: vertical joints that make the perspective of each wall readable
  const joints = Array.from({ length: 6 }, (_, index) => 0.2 + index * 0.85);
  const jointLine = (x: number, z: number): string =>
    worldPoints(cam, [
      [x, slope(z, 0.15), z],
      [x, ceiling, z],
    ]);

  // handrail on the right wall: along the nosings, then level across the landing
  const railZs = [0, ...Array.from({ length: steps - 1 }, (_, i) => stepZ(i + 1)), zBack - 0.5];
  const rail = railZs.map((z) => P(half - 0.04, slope(z, 0.92), z));

  // wall lights on the left wall
  const sconces = [0.7, 1.7, 2.9].map((z) => ({ at: P(-half + 0.03, slope(z, 1.3), z), z }));

  return (
    <g>
      <defs>
        <linearGradient id={id('left')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#38342f" />
          <stop offset="1" stopColor="#6b6458" />
        </linearGradient>
        <linearGradient id={id('right')} x1="1" y1="0" x2="0" y2="0">
          <stop offset="0" stopColor="#34302c" />
          <stop offset="1" stopColor="#5f594f" />
        </linearGradient>
        <linearGradient id={id('ceil')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#161514" />
          <stop offset="1" stopColor="#35312d" />
        </linearGradient>
        <linearGradient id={id('back')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4a453f" />
          <stop offset="1" stopColor="#6a645a" />
        </linearGradient>
        <linearGradient id={id('door')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff6e2" />
          <stop offset="1" stopColor="#ffd9a1" />
        </linearGradient>
        <linearGradient id={id('brass')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={PAL.brassHi} />
          <stop offset="0.5" stopColor={PAL.brass} />
          <stop offset="1" stopColor={PAL.brassLo} />
        </linearGradient>
        <radialGradient id={id('sconce')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={PAL.warm} stopOpacity="0.85" />
          <stop offset="0.45" stopColor={PAL.warm} stopOpacity="0.22" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id('doorglow')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#fff1d6" stopOpacity="0.8" />
          <stop offset="1" stopColor={PAL.warm} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill="#161514" />
      <polygon points={worldPoints(cam, ceilingPoly)} fill={ref('ceil')} />
      <polygon points={toPoints(backWall)} fill={ref('back')} />
      <polygon points={worldPoints(cam, leftWall)} fill={ref('left')} />
      <polygon points={worldPoints(cam, rightWall)} fill={ref('right')} />

      {/* panel joints on both walls */}
      <g stroke="#000" strokeOpacity="0.3" strokeWidth="1.5" fill="none">
        {joints.map((z) => (
          <g key={z}>
            <polyline points={jointLine(-half, z)} />
            <polyline points={jointLine(half, z)} />
          </g>
        ))}
      </g>

      {/* skirting following the steps */}
      <polygon points={skirt(-half)} fill="#12100f" />
      <polygon points={skirt(half)} fill="#12100f" />

      {/* the light at the top of the stairs */}
      <ellipse cx={doorLight[0]} cy={doorLight[1]} rx="210" ry="150" fill={ref('doorglow')} />
      <polygon points={toPoints(doorway)} fill={ref('door')} />

      {/* wall lights, each with a pool on the wall */}
      {sconces.map(({ at, z }) => {
        const scale = 880 / (z + 2.4);
        return (
          <g key={z}>
            <ellipse
              cx={at[0] + scale * 0.18}
              cy={at[1]}
              rx={scale * 0.5}
              ry={scale * 0.75}
              fill={ref('sconce')}
            />
            <rect
              x={at[0]}
              y={at[1] - scale * 0.1}
              width={scale * 0.06}
              height={scale * 0.2}
              rx="1.5"
              fill={ref('brass')}
            />
          </g>
        );
      })}

      {/* handrail */}
      <polyline
        points={rail.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
        fill="none"
        stroke={PAL.brassLo}
        strokeWidth="6.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <polyline
        points={rail.map(([x, y]) => `${x.toFixed(1)},${(y - 0.8).toFixed(1)}`).join(' ')}
        fill="none"
        stroke={PAL.brassHi}
        strokeWidth="2.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </g>
  );
}

// ── Shade ───────────────────────────────────────────────────────────────────────────────────────

export function StaircaseShade({ ids }: ArtProps) {
  const { id } = scope(ids, 'shade');
  return (
    <g>
      <defs>
        <linearGradient
          id={id('depth')}
          x1="0"
          y1="250"
          x2="0"
          y2="800"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0" />
          <stop offset="0.6" stopColor="#000" stopOpacity="0.1" />
          <stop offset="1" stopColor="#000" stopOpacity="0.42" />
        </linearGradient>
        <linearGradient
          id={id('sides')}
          x1="350"
          y1="0"
          x2="850"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#000" stopOpacity="0.34" />
          <stop offset="0.3" stopColor="#000" stopOpacity="0" />
          <stop offset="0.7" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.34" />
        </linearGradient>
      </defs>
      {staircaseRegions.map((region) => {
        const points = toPoints(overlayQuad(region));
        return (
          <g key={region.id}>
            <polygon
              points={points}
              fill="#000"
              opacity={region.id.startsWith('riser') ? 0.32 : 0.04}
            />
            <polygon points={points} fill={`url(#${id('depth')})`} />
            <polygon points={points} fill={`url(#${id('sides')})`} />
          </g>
        );
      })}
    </g>
  );
}

// ── Sheen ───────────────────────────────────────────────────────────────────────────────────────

export function StaircaseSheen({ ids }: ArtProps) {
  const { id } = scope(ids, 'sheen');
  const doorLight = P(0, landingY, zBack);
  return (
    <g>
      <defs>
        <filter id={id('soft')} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="24" />
        </filter>
        <filter id={id('fine')} x="-10%" y="-200%" width="120%" height="500%">
          <feGaussianBlur stdDeviation="1.2" />
        </filter>
        <linearGradient
          id={id('spill')}
          x1="0"
          y1={doorLight[1]}
          x2="0"
          y2="800"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#fff" stopOpacity="0.9" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.3" />
          <stop offset="1" stopColor="#fff" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {/* light from the doorway runs down the flight */}
      <polygon
        points={`${(doorLight[0] - 70).toFixed(0)},${doorLight[1].toFixed(0)} ${(doorLight[0] + 70).toFixed(0)},${doorLight[1].toFixed(0)} 860,800 340,800`}
        fill={`url(#${id('spill')})`}
        filter={`url(#${id('soft')})`}
        opacity="0.8"
      />
      <ellipse
        cx={doorLight[0]}
        cy={doorLight[1] + 10}
        rx="150"
        ry="26"
        fill={PAL.warm}
        opacity="0.7"
        filter={`url(#${id('soft')})`}
      />
      {/* every nosing catches the light */}
      {Array.from({ length: steps }, (_, i) => {
        const a = P(-half, stepY(i + 1), stepZ(i));
        const b = P(half, stepY(i + 1), stepZ(i));
        return (
          <line
            key={i}
            x1={a[0]}
            y1={a[1]}
            x2={b[0]}
            y2={b[1]}
            stroke="#fff"
            strokeWidth="2.2"
            opacity="0.9"
            filter={`url(#${id('fine')})`}
          />
        );
      })}
    </g>
  );
}

// ── Front: the landing's furniture and the room's vignette ──────────────────────────────────────

export function StaircaseFront({ ids }: ArtProps) {
  const { id, ref } = scope(ids, 'front');
  const rng = seeded(14);

  const bench = boxFaces(cam, {
    x0: 0.25,
    x1: 1.1,
    y0: landingY,
    y1: landingY + 0.44,
    z0: 4.3,
    z1: 4.85,
  });

  const pot = P(-0.82, landingY, 4.3);
  const potTop = P(-0.82, landingY + 0.34, 4.3);
  const scale = 880 / (4.3 + 2.4);
  const leaves = Array.from({ length: 16 }, (_, index) => ({
    d: leaf(
      potTop[0],
      potTop[1],
      -90 + (index - 7.5) * 12 + (rng() - 0.5) * 14,
      (0.5 + rng() * 0.8) * scale,
      (0.08 + rng() * 0.06) * scale,
    ),
    fill: index % 3 === 0 ? PAL.leafHi : index % 3 === 1 ? PAL.leaf : PAL.leafLo,
  }));

  return (
    <g>
      <defs>
        <linearGradient id={id('wood')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#5a4430" />
          <stop offset="1" stopColor="#2a1f16" />
        </linearGradient>
        <linearGradient id={id('pot')} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#d6c9ac" />
          <stop offset="1" stopColor="#9c8e70" />
        </linearGradient>
        <radialGradient
          id={id('vignette')}
          cx="600"
          cy="420"
          r="780"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(600 420) scale(1 0.74) translate(-600 -420)"
        >
          <stop offset="0.5" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.62" />
        </radialGradient>
        <filter id={id('shadow')} x="-30%" y="-60%" width="160%" height="220%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>

      {/* bench on the landing */}
      <ellipse
        cx={(bench.front[0]?.[0] ?? 0) + 40}
        cy={(bench.front[3]?.[1] ?? 0) + 3}
        rx="70"
        ry="9"
        fill="#000"
        opacity="0.5"
        filter={`url(#${id('shadow')})`}
      />
      {bench.left ? <polygon points={toPoints(bench.left)} fill="#2a1f16" /> : null}
      <polygon points={toPoints(bench.front)} fill={ref('wood')} />
      {bench.top ? <polygon points={toPoints(bench.top)} fill="#6e563f" /> : null}

      {/* plant */}
      <ellipse
        cx={pot[0]}
        cy={pot[1] + 1}
        rx={scale * 0.36}
        ry="6"
        fill="#000"
        opacity="0.5"
        filter={`url(#${id('shadow')})`}
      />
      {leaves.map((item, index) => (
        <path key={index} d={item.d} fill={item.fill} />
      ))}
      <path
        d={`M${potTop[0] - scale * 0.2} ${potTop[1]}L${potTop[0] + scale * 0.2} ${potTop[1]}L${pot[0] + scale * 0.14} ${pot[1]}L${pot[0] - scale * 0.14} ${pot[1]}Z`}
        fill={ref('pot')}
      />

      <rect width={DESIGN_WIDTH} height={DESIGN_HEIGHT} fill={ref('vignette')} />
    </g>
  );
}
